/**
 * **Grup modifier yang berlaku untuk satu produk** — bagian kedua dari pasangan Modifier.
 *
 * ### ⭐⭐ Kenapa MEMILIH ada di sini, sementara MEMBUAT ada di layar master
 * Grup dipakai banyak produk ("Tingkat pedas" melekat pada 20 menu) ⇒ membuatnya milik layar
 * tersendiri. Tapi **grup mana yang berlaku** adalah keputusan tentang produk ini — dan orang yang
 * menjawabnya sedang menatap produk itu, bukan sedang menata daftar grup.
 *
 * ⚠️ Pembagian yang sama sudah dipakai untuk Struktur vs Produk; menyatukannya berarti "Pedas"
 * dibuat lima kali untuk lima menu.
 *
 * ### ⛔ Mencabut grup TIDAK menyentuh riwayat
 * `product_modifier_group` adalah tabel KAITAN — "berlaku atau tidak" satu-satunya keadaannya.
 * Baris pesanan yang sudah terlanjur memakai modifier itu menyimpan potretnya sendiri, jadi
 * mencabut di sini tak pernah mengubah struk yang sudah tercetak.
 */
import { Alert, Card, Empty, Space, Switch, Table, Tag, message } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import { canWrite } from '../api/session';

interface GroupRow {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly min_select: number;
  readonly max_select: number;
  readonly is_active: boolean;
}
interface LinkRow {
  readonly id: string;
  readonly product_id: string;
  readonly group_id: string;
}

export function ProductModifiers({ productId }: { readonly productId: string }) {
  const { t } = useTranslation();
  const boleh = canWrite();
  const [groups, setGroups] = useState<readonly GroupRow[]>([]);
  const [links, setLinks] = useState<readonly LinkRow[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  const muat = useCallback(async () => {
    const [g, l] = await Promise.all([
      /* ⛔ Hanya grup AKTIF yang bisa dipasang — memasang yang nonaktif membuat kasir melihat
         pilihan yang tak pernah muncul di layarnya. */
      api.listMaster<GroupRow>('modifier-groups'),
      api.listMaster<LinkRow>('product-modifier-groups', { product_id: productId }),
    ]);
    setGroups(g);
    setLinks(l);
  }, [productId]);

  useEffect(() => { void muat().catch(() => undefined); }, [muat]);

  async function ubah(group: GroupRow, nyala: boolean): Promise<void> {
    setBusy(group.id);
    try {
      if (nyala) {
        await api.createMaster('product-modifier-groups', { productId, groupId: group.id });
      } else {
        const baris = links.find((x) => x.group_id === group.id);
        if (baris !== undefined) await api.deleteMaster('product-modifier-groups', baris.id);
      }
      await muat();
    } catch (e) {
      void message.error(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  const terpasang = new Set(links.map((l) => l.group_id));

  return (
    <Card size="small" title={t('modifier.forProduct')} data-testid="produk-modifier"
          style={{ marginTop: 16 }}>
      {groups.length === 0 ? (
        /* ⚠️ Menyebut ke mana harus pergi — kotak kosong tanpa kalimat terbaca sebagai kerusakan. */
        <Empty description={t('modifier.noGroupYet')} data-testid="produk-modifier-kosong" />
      ) : (
        <Space orientation="vertical" size="small" style={{ width: '100%' }}>
          {/*
            ⛔ Grup WAJIB yang dipasang mengubah alur kasir: baris tak bisa disimpan sampai dijawab.
            Disebut di depan supaya keputusannya diambil sadar, bukan ditemukan saat jam sibuk.
          */}
          <Alert type="info" showIcon title={t('modifier.attachWarning')} />
          <Table<GroupRow>
            size="small"
            pagination={false}
            rowKey={(g) => g.id}
            dataSource={[...groups]}
            columns={[
              {
                title: t('master.code'),
                dataIndex: 'code',
                width: 160,
                render: (v: string) => <b data-testid={`pm-${v}`}>{v}</b>,
              },
              { title: t('master.name'), dataIndex: 'name' },
              {
                title: t('modifier.rule'),
                key: 'aturan',
                width: 200,
                render: (_: unknown, g: GroupRow) =>
                  g.min_select > 0
                    ? <Tag color="red">{t('modifier.required', { n: g.min_select })}</Tag>
                    : <Tag>{t('modifier.optional')}</Tag>,
              },
              {
                title: t('modifier.applies'),
                key: 'pakai',
                width: 120,
                render: (_: unknown, g: GroupRow) => (
                  <Switch
                    checked={terpasang.has(g.id)}
                    disabled={!boleh || busy === g.id}
                    data-testid={`pm-pakai-${g.code}`}
                    checkedChildren={t('common.on')}
                    unCheckedChildren={t('common.off')}
                    onChange={(v) => void ubah(g, v)}
                  />
                ),
              },
            ]}
          />
        </Space>
      )}
    </Card>
  );
}
